import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Dashboard from './pages/Dashboard';
import ScanManagement from './pages/ScanManagement';
import ScanHistory from './pages/ScanHistory';
import Reports from './pages/Reports';
import Branding from './pages/Branding';
import Billing from './pages/Billing';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DemoPage from './pages/DemoPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import NotFoundPage from './pages/NotFoundPage';
import BlogIndex from './pages/BlogIndex';
import BlogFuseguVsWordfence from './pages/BlogFuseguVsWordfence';
import BlogFuseguVsSucuri from './pages/BlogFuseguVsSucuri';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BeagleSecurityAlternative from './pages/BeagleSecurityAlternative';
import WordfenceAlternative from './pages/WordfenceAlternative';
import SucuriAlternative from './pages/SucuriAlternative';
import SiteLockAlternative from './pages/SiteLockAlternative';
import IThemesSecurityAlternative from './pages/IThemesSecurityAlternative';

// Protected route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// App routes component (needs to be inside AuthProvider)
function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <RegisterPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/blogs" element={<BlogIndex />} />
      <Route path="/blogs/fusegu-vs-wordfence" element={<BlogFuseguVsWordfence />} />
      <Route path="/blogs/fusegu-vs-sucuri" element={<BlogFuseguVsSucuri />} />

      {/* Alternative pages */}
      <Route path="/beagle-security-alternative" element={<BeagleSecurityAlternative />} />
      <Route path="/wordfence-alternative" element={<WordfenceAlternative />} />
      <Route path="/sucuri-alternative" element={<SucuriAlternative />} />
      <Route path="/sitelock-alternative" element={<SiteLockAlternative />} />
      <Route path="/ithemes-security-alternative" element={<IThemesSecurityAlternative />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/scans" element={
        <ProtectedRoute>
          <ScanManagement />
        </ProtectedRoute>
      } />
      <Route path="/history" element={
        <ProtectedRoute>
          <ScanHistory />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <Reports />
        </ProtectedRoute>
      } />
      <Route path="/branding" element={
        <ProtectedRoute>
          <Branding />
        </ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute>
          <Billing />
        </ProtectedRoute>
      } />
      <Route path="/upgrade" element={
        <ProtectedRoute>
          <Billing />
        </ProtectedRoute>
      } />

      {/* Root route - demo page always accessible for SEO */}
      <Route path="/" element={<DemoPage />} />

      {/* 404 catch-all route */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <div className="min-h-screen bg-background">
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;